import { useAppSelector } from "@/hooks/useAppSelector";
import { useNPVSettingsUpsert } from "@/hooks/useNPVSettingsUpsert";
import { useUniquePossibilitiesUpsert } from "@/hooks/useUniquePossibilitiesUpsert";
import { npvActions } from "@/redux/npv/npv-slice";
import { uqActions } from "@/redux/uq/uq-slice";
import CustomButton from "@components/CustomButton";
import { useQueryClient } from "@tanstack/react-query";
import { Popup } from "devextreme-react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";

type Props = {
  restoreUp?: boolean;
  restoreUpFn?: () => void;
  upData?: any;
};

export default function SaveChangesPopup({ restoreUp, restoreUpFn, upData }: Props) {
  const { id } = useParams();
  const qc = useQueryClient();
  const {
    isModified: isUpModified,
    gridSet,
    settings,
    modifiedOn,
    headerTitles: titles,
    showRestorePopup,
  } = useAppSelector((state) => state.uq);
  const { household } = useAppSelector((state) => state.household);
  const { isModified: isNpvModified, settings: npvSettings } = useAppSelector((state) => state.npv);
  const { mutate: upsertUq } = useUniquePossibilitiesUpsert();
  const { mutateAsync: saveNPVSettings } = useNPVSettingsUpsert();
  const dispatch = useDispatch();

  const saveUQ = async () => {
    upsertUq({
      data: {
        modifiedOn: modifiedOn,
        modifiedBy: upData?.modifiedBy,
        household: {
          externalCode: id as string,
        },
        data: JSON.stringify({
          gridSet,
          settings,
          household,
          titles,
        }),
      },
      overwrite: true,
    });
  };

  return (
    <Popup
      width={448}
      height={"fit"}
      visible={showRestorePopup}
      showTitle={false}
      showCloseButton={false}
      className="shadow-light"
    >
      {isUpModified && restoreUp && restoreUpFn ? (
        <div className="flex flex-col gap-5">
          <span className="block text-center text-lg font-medium">
            Do you want to restore changes?
          </span>
          <div className="flex justify-center gap-4">
            <CustomButton
              text="Cancel"
              onClickHandler={() => {
                dispatch(uqActions.setShowRestorePopup(false));
              }}
              className="w-[7.625rem] !px-2"
            />
            <CustomButton
              text="Restore"
              variant="primary"
              onClickHandler={() => {
                dispatch(uqActions.setShowRestorePopup(false));
                restoreUpFn();
              }}
              className="w-[7.625rem] !px-2"
            />
          </div>
        </div>
      ) : null}
      {isUpModified && !restoreUp ? (
        <div className="flex flex-col gap-5">
          <span className="block text-center text-lg font-medium">
            Do you want to save changes?
          </span>
          <div className="flex justify-center gap-4">
            <CustomButton
              text="Cancel"
              onClickHandler={() => {
                dispatch(uqActions.setShowRestorePopup(false));
              }}
              className="w-[7.625rem] !px-2"
            />
            <CustomButton
              text="Don't save"
              onClickHandler={() => {
                dispatch(uqActions.setShowRestorePopup(false));
                setTimeout(() => {
                  qc.refetchQueries();
                  dispatch(uqActions.setIsModified(false));
                }, 100);
              }}
              className="w-[7.625rem] !px-2"
            />
            <CustomButton
              variant="primary"
              onClickHandler={() => {
                saveUQ().then(() => {
                  dispatch(uqActions.setShowRestorePopup(false));
                  dispatch(uqActions.setIsModified(false));
                });
              }}
              text="Save"
              className="w-[7.625rem] !px-2"
            />
          </div>
        </div>
      ) : null}

      {isNpvModified ? (
        <div className="flex flex-col gap-5">
          <span className="block text-center text-lg font-medium">
            Do you want to save changes?
          </span>
          <div className="flex justify-center gap-4">
            <CustomButton
              text="Cancel"
              onClickHandler={() => {
                dispatch(uqActions.setShowRestorePopup(false));
              }}
              className="w-[7.625rem] !px-2"
            />
            <CustomButton
              text="Don't save"
              className="w-[7.625rem] !px-2"
              onClickHandler={() => {
                dispatch(uqActions.setShowRestorePopup(false));
                setTimeout(() => {
                  dispatch(npvActions.setIsModified(false));
                  qc.refetchQueries();
                }, 100);
              }}
            />
            <CustomButton
              variant="primary"
              className="w-[7.625rem] !px-2"
              onClickHandler={() => {
                dispatch(uqActions.setShowRestorePopup(false));
                saveNPVSettings({
                  data: JSON.stringify({
                    yearsToRetire: npvSettings.yearsToRetire,
                    npvAge: npvSettings.npvAge,
                    irr: npvSettings.irr,
                  }),
                  householdId: upData?.household?.externalCode,
                }).then(() => {
                  dispatch(npvActions.setIsModified(false));
                });
              }}
              text="Save"
            />
          </div>
        </div>
      ) : null}
    </Popup>
  );
}
